import { getDocChatTurnStatus } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatConversation'
import {
  type DocChatStreamUpdate,
  subscribeDocChatStreamUpdates,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatInFlightStore'
import type { DocumentTab } from 'features/knowledgeDocumentViewer/types'
import { agenticConversationSessionKey } from 'features/knowledgeDocumentViewer/utils/agenticConversationIdentity'
import { getKeepAliveSearchHistoryEntry } from 'features/knowledgeDocumentViewer/utils/keepAliveSearchTab'
import { type Dispatch, type SetStateAction, useEffect } from 'react'

export function applySettledDocChatStreamUpdate(
  openTabs: DocumentTab[],
  update: DocChatStreamUpdate
): DocumentTab[] {
  const turn = update.conversation.turns.find(
    (turn) => turn.id === update.conversation.activeTurnId
  )
  if (!turn) return openTabs
  if (getDocChatTurnStatus(turn) === 'loading' && !turn.restoredRunning) {
    return openTabs
  }
  let changed = false
  const nextTabs = openTabs.map((tab) => {
    const retainedSearch = getKeepAliveSearchHistoryEntry(tab)
    if (!retainedSearch) return tab
    const { item: searchItem, index: historyIndex } = retainedSearch
    const docChat = searchItem.searchState.docChat
    if (
      !docChat ||
      docChat.siteId !== update.siteId ||
      docChat.conversation.activeTurnId !== turn.id
    ) {
      return tab
    }
    const sessionKey = agenticConversationSessionKey(
      docChat.conversation,
      tab.id
    )
    if (tab.id !== update.tabId && !update.keys.includes(sessionKey)) return tab
    if (docChat.conversation === update.conversation) return tab
    const history = [...tab.history]
    history[historyIndex] = {
      ...searchItem,
      searchState: {
        ...searchItem.searchState,
        docChat: {
          ...docChat,
          conversation: update.conversation,
          isHydratedHistory: true,
        },
      },
    }
    changed = true
    return { ...tab, history }
  })
  return changed ? nextTabs : openTabs
}

export function useSyncDocChatStreamUpdates({
  siteId,
  setOpenTabs,
}: {
  siteId: string
  setOpenTabs: Dispatch<SetStateAction<DocumentTab[]>>
}): void {
  useEffect(
    () =>
      subscribeDocChatStreamUpdates((update) => {
        if (update.siteId !== siteId) return
        setOpenTabs((openTabs) =>
          applySettledDocChatStreamUpdate(openTabs, update)
        )
      }),
    [setOpenTabs, siteId]
  )
}
